/**
 * Cart drawer — slide-in panel with fetch-based qty/remove.
 *
 * Triggers:
 *   - [data-cart-drawer-toggle]   click → open drawer
 *   - [data-cart-drawer-close]    click → close
 *   - [data-cart-drawer-backdrop] click → close
 *   - ESC key                     close
 *
 * Cart mutations (qty +/-, remove) inside the drawer use Shopify's
 * /cart/change.js JSON endpoint. After each mutation, the drawer body
 * is re-rendered via Shopify's Section Rendering API
 * (/cart?section_id=cart-drawer) so the markup stays in sync without
 * a full page reload.
 */

(function () {
  'use strict';

  const drawer = document.querySelector('[data-cart-drawer]');
  const backdrop = document.querySelector('[data-cart-drawer-backdrop]');
  if (!drawer || !backdrop) return;

  const triggers = document.querySelectorAll('[data-cart-drawer-toggle]');
  const closeBtn = drawer.querySelector('[data-cart-drawer-close]');

  // ---------- frozen handling fee ($5 NZD) ----------
  //
  // Auto-add a hidden fee product whenever the cart contains any frozen
  // item; auto-remove it when the last frozen item leaves. The fee is a
  // real Shopify product (hidden from Online Store sales channel) so it
  // appears as a normal line item and gets taxed/shipped like any other.
  // Identify fee lines via either variant ID or the _is_frozen_fee
  // line-item property — belt-and-braces against future variant ID drift.
  const FROZEN_FEE_VARIANT_ID = 51283890110742;
  const FROZEN_FEE_PROPERTY = '_is_frozen_fee';

  function isFeeLine(line) {
    if (line.variant_id === FROZEN_FEE_VARIANT_ID) return true;
    if (line.properties && line.properties[FROZEN_FEE_PROPERTY] === 'true') return true;
    return false;
  }

  // Server-side truth: fetch the cart-drawer section and parse the JSON
  // script tag emitted by sections/cart-drawer.liquid. Returns an array
  // of 1-based line indices that are frozen. Returns null on failure
  // (caller should fall back to property-based detection).
  //
  // Cache-busted: browsers may cache GET /cart?section_id=cart-drawer
  // by URL, which served stale data after a /cart/change.js mutation
  // and prevented the reconciler from removing the fee when the last
  // frozen item was removed. `cache: 'no-store'` + a unique `t=` query
  // param force a fresh fetch on every call.
  async function fetchFrozenLineIndicesFromServer() {
    try {
      const res = await fetch('/cart?section_id=cart-drawer&t=' + Date.now(), {
        headers: { Accept: 'text/html' },
        cache: 'no-store',
      });
      if (!res.ok) return null;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const tag = doc.querySelector('[data-cart-frozen-lines]');
      if (!tag) return null;
      const arr = JSON.parse(tag.textContent || '[]');
      return Array.isArray(arr) ? arr : null;
    } catch (_) {
      return null;
    }
  }

  // Serialize all reconcile calls. If two cart mutations land in quick
  // succession (rapid Add-to-Cart clicks), both callers' /cart.js
  // snapshots can be stale and both might decide "no fee yet, add one"
  // — Shopify then merges the duplicate adds into qty=2 and the fee
  // doubles. By chaining on a single promise, only one reconcile runs
  // at a time. Each one re-fetches /cart.js inside the lock so it sees
  // the latest cart state.
  let reconcileChain = Promise.resolve(false);

  async function _doReconcile() {
    // Re-fetch /cart.js fresh inside the lock so we see the latest
    // state, even if a sibling mutation just landed.
    let cart;
    try {
      const r = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!r.ok) return false;
      cart = await r.json();
    } catch (_) {
      return false;
    }
    if (!cart || !Array.isArray(cart.items)) return false;

    let frozenCount;
    const serverIndices = await fetchFrozenLineIndicesFromServer();
    if (Array.isArray(serverIndices)) {
      frozenCount = serverIndices.length;
    } else {
      frozenCount = cart.items.filter(
        (line) => !isFeeLine(line) && line.properties && line.properties._frozen === 'true'
      ).length;
    }

    const feeLines = cart.items
      .map((line, i) => ({ line, idx: i + 1 }))
      .filter(({ line }) => isFeeLine(line));

    const post = (path, body) => fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    const removeLines = async (entries) => {
      for (const { idx } of entries.sort((a, b) => b.idx - a.idx)) {
        try { await post('/cart/change.js', { line: idx, quantity: 0 }); } catch (_) {}
      }
    };

    if (frozenCount > 0 && feeLines.length === 0) {
      try {
        await post('/cart/add.js', {
          id: FROZEN_FEE_VARIANT_ID,
          quantity: 1,
          properties: { [FROZEN_FEE_PROPERTY]: 'true' },
        });
        return true;
      } catch (_) { return false; }
    }
    if (frozenCount === 0 && feeLines.length > 0) {
      await removeLines(feeLines);
      return true;
    }
    if (feeLines.length > 1) {
      await removeLines(feeLines.slice(1));
      return true;
    }
    if (feeLines.length === 1 && feeLines[0].line.quantity !== 1) {
      try {
        await post('/cart/change.js', { line: feeLines[0].idx, quantity: 1 });
        return true;
      } catch (_) { return false; }
    }
    return false;
  }

  // Public entry — wraps _doReconcile in the serialization chain.
  // Caller's `cart` param is now ignored (we always re-fetch fresh).
  async function reconcileFrozenFee(_cart) {
    const next = reconcileChain.catch(() => false).then(_doReconcile);
    reconcileChain = next.catch(() => false);
    return next;
  }

  // ---------- open / close ----------
  //
  // Tracks the element that opened the drawer so focus returns
  // there on close — essential keyboard-nav behaviour. Refcounted
  // body-scroll lock so the drawer + modal don't release the lock
  // out from under each other when one closes while the other is open.

  let lastFocusBeforeDrawer = null;
  let scrollLockCount = 0;

  function lockScroll() {
    if (scrollLockCount === 0) document.documentElement.style.overflow = 'hidden';
    scrollLockCount++;
  }
  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) document.documentElement.style.overflow = '';
  }

  // Tab-key focus trap: on Tab, find all focusable descendants of `el`
  // and wrap focus around when reaching either edge.
  function trapTab(el, e) {
    if (e.key !== 'Tab') return;
    const focusable = el.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) { e.preventDefault(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  function open() {
    lastFocusBeforeDrawer = document.activeElement;
    drawer.removeAttribute('hidden');
    backdrop.removeAttribute('hidden');
    // next frame so transitions kick in
    requestAnimationFrame(() => {
      drawer.setAttribute('data-open', '');
      backdrop.setAttribute('data-open', '');
      drawer.setAttribute('aria-hidden', 'false');
      lockScroll();
      triggers.forEach((t) => t.setAttribute('aria-expanded', 'true'));
      // Move focus into the drawer (first focusable, fall back to close btn)
      const target = drawer.querySelector('[data-cart-drawer-close]') || drawer;
      try { target.focus({ preventScroll: true }); } catch (_) {}
    });
  }

  function close() {
    drawer.removeAttribute('data-open');
    backdrop.removeAttribute('data-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();
    triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    // return focus to the trigger element that opened us
    if (lastFocusBeforeDrawer && typeof lastFocusBeforeDrawer.focus === 'function') {
      try { lastFocusBeforeDrawer.focus({ preventScroll: true }); } catch (_) {}
    }
    lastFocusBeforeDrawer = null;
  }

  triggers.forEach((t) => {
    t.addEventListener('click', (e) => {
      e.preventDefault();
      open();
    });
  });
  if (closeBtn) closeBtn.addEventListener('click', close);
  backdrop.addEventListener('click', close);

  // Combined keydown: ESC closes whichever overlay is on top (modal > drawer);
  // Tab inside the drawer is trapped so focus can't escape underneath.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (modalEl && modalEl.hasAttribute('data-open')) { closeModal(); return; }
      if (drawer.hasAttribute('data-open')) { close(); return; }
    }
    if (e.key === 'Tab') {
      if (modalEl && modalEl.hasAttribute('data-open')) trapTab(modalEl, e);
      else if (drawer.hasAttribute('data-open')) trapTab(drawer, e);
    }
  });

  // ---------- header count badge sync ----------

  function updateHeaderCount(itemCount) {
    const trolley = document.querySelector('.site-header__trolley');
    if (!trolley) return;
    let badge = trolley.querySelector('.site-header__cart-count');
    if (itemCount > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'site-header__cart-count';
        trolley.appendChild(badge);
      }
      badge.textContent = itemCount;
    } else if (badge) {
      badge.remove();
    }
  }

  // ---------- drawer body refresh ----------

  async function refreshDrawer() {
    // Snapshot the focused element so a section refresh that lands while
    // a customer is mid-typing in a qty input doesn't destroy their input.
    const active = document.activeElement;
    const inDrawer = active && drawer.contains(active);
    const focusKey = inDrawer ? (active.dataset?.qtyInput || null) : null;
    const focusSelStart = (inDrawer && active.selectionStart != null) ? active.selectionStart : null;
    const focusSelEnd = (inDrawer && active.selectionEnd != null) ? active.selectionEnd : null;

    try {
      // Cache-busted (see fetchFrozenLineIndicesFromServer for context).
      const res = await fetch('/cart?section_id=cart-drawer&t=' + Date.now(), {
        headers: { Accept: 'text/html' },
        cache: 'no-store',
      });
      if (!res.ok) return;
      const html = await res.text();
      // Parse via DOMParser instead of innerHTML round-trip so untrusted
      // line-item properties / variant titles can't trigger inline-script
      // execution paths even in the unlikely case Shopify's escaping changes.
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newBody = doc.querySelector('[data-cart-drawer-body]');
      const currentBody = drawer.querySelector('[data-cart-drawer-body]');
      if (newBody && currentBody) {
        currentBody.replaceChildren(...Array.from(newBody.childNodes));
      }

      // Restore focus to the same logical input if it still exists.
      if (focusKey) {
        const restored = drawer.querySelector('[data-qty-input="' + focusKey + '"]');
        if (restored) {
          try {
            restored.focus({ preventScroll: true });
            if (focusSelStart != null && typeof restored.setSelectionRange === 'function') {
              restored.setSelectionRange(focusSelStart, focusSelEnd != null ? focusSelEnd : focusSelStart);
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      /* swallow — fall back to no-op */
    }
  }

  // ---------- qty + remove handlers ----------

  // Cache of debounce timers per line index
  const qtyTimers = {};

  function changeLine(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    })
      .then((r) => r.json())
      .then(async (cart) => {
        updateHeaderCount(cart.item_count);
        const mutated = await reconcileFrozenFee(cart);
        if (mutated) {
          try {
            const fresh = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
            updateHeaderCount(fresh.item_count);
          } catch (_) {}
        }
        return refreshDrawer();
      });
  }

  // Delegated click: qty +/- and remove links inside the drawer
  drawer.addEventListener('click', (e) => {
    const dec = e.target.closest('[data-qty-decrease]');
    const inc = e.target.closest('[data-qty-increase]');
    const remove = e.target.closest('[data-cart-remove]');

    if (dec || inc) {
      const idx = (dec || inc).getAttribute('data-qty-decrease') || (dec || inc).getAttribute('data-qty-increase');
      const line = parseInt(idx, 10);
      const input = drawer.querySelector('[data-qty-input="' + idx + '"]');
      if (!input) return;
      let n = parseInt(input.value, 10) || 0;
      n = inc ? n + 1 : Math.max(0, n - 1);
      input.value = n;
      // debounce so rapid clicks coalesce to a single network call
      clearTimeout(qtyTimers[idx]);
      qtyTimers[idx] = setTimeout(() => changeLine(line, n), 220);
      return;
    }

    if (remove) {
      e.preventDefault();
      const line = parseInt(remove.getAttribute('data-cart-remove'), 10);
      if (!Number.isFinite(line)) return;
      changeLine(line, 0);
    }
  });

  // Manual numeric input (typed quantity)
  drawer.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty-input]');
    if (!input) return;
    const idx = input.getAttribute('data-qty-input');
    const line = parseInt(idx, 10);
    const n = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(line, n);
  });

  // Persist gift message on blur
  drawer.addEventListener('change', (e) => {
    const note = e.target.closest('[data-cart-note]');
    if (!note) return;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: note.value }),
    });
  });

  // ---------- product-card "Add to Trolley" button ----------
  //
  // Mixed carts (frozen + ambient) are allowed. Customers see a
  // shipping notice on the cart for frozen items and Shopify charges
  // both shipping profiles' rates at checkout (one parcel each).
  //
  // Shopify cart-validation Functions also run on /cart/add. If the
  // server rejects the add (e.g. SI address + frozen item), we parse
  // the JSON response and surface the validation message as a toast
  // instead of letting the form fall through to a full-page error.

  let modalEl = null;
  let modalBackdropEl = null;
  let modalBodyEl = null;
  let modalCloseBtn = null;
  let lastFocusBeforeModal = null;

  function ensureModalDom() {
    if (modalEl) return;

    modalBackdropEl = document.createElement('div');
    modalBackdropEl.className = 'cart-modal-backdrop';
    document.body.appendChild(modalBackdropEl);

    modalEl = document.createElement('div');
    modalEl.className = 'cart-modal';
    modalEl.setAttribute('role', 'alertdialog');
    modalEl.setAttribute('aria-modal', 'true');
    modalEl.setAttribute('aria-labelledby', 'cart-modal-title');
    // Build via DOM APIs (not innerHTML) so the static markup never
    // interpolates anything user-controlled.
    const icon = document.createElement('div');
    icon.className = 'cart-modal__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '🧊';
    const title = document.createElement('h2');
    title.id = 'cart-modal-title';
    title.className = 'cart-modal__title';
    title.textContent = "This frozen order can't be shipped to that address";
    const body = document.createElement('p');
    body.className = 'cart-modal__body';
    body.dataset.cartModalBody = '';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cart-modal__btn';
    btn.dataset.cartModalClose = '';
    btn.textContent = 'Understood';
    modalEl.append(icon, title, body, btn);
    document.body.appendChild(modalEl);

    modalBodyEl = body;
    modalCloseBtn = btn;

    modalCloseBtn.addEventListener('click', closeModal);
    modalBackdropEl.addEventListener('click', closeModal);
    // ESC + Tab handled by the unified keydown listener above.
  }

  function showModal(message) {
    ensureModalDom();
    lastFocusBeforeModal = document.activeElement;
    modalBodyEl.textContent = message;
    lockScroll();
    requestAnimationFrame(() => {
      modalBackdropEl.setAttribute('data-open', '');
      modalEl.setAttribute('data-open', '');
      try { modalCloseBtn.focus({ preventScroll: true }); } catch (_) {}
    });
  }

  function closeModal() {
    if (!modalEl) return;
    modalBackdropEl.removeAttribute('data-open');
    modalEl.removeAttribute('data-open');
    unlockScroll();
    if (lastFocusBeforeModal && typeof lastFocusBeforeModal.focus === 'function') {
      try { lastFocusBeforeModal.focus({ preventScroll: true }); } catch (_) {}
    }
    lastFocusBeforeModal = null;
  }

  // Back-compat alias for the rest of the file (paths still call showToast).
  const showToast = showModal;

  async function parseAddError(res) {
    // Shopify returns { status, message, description } or
    // { errors: [...] } on /cart/add.js failures. Walk both shapes.
    try {
      const body = await res.clone().json();
      if (body && body.description) return body.description;
      if (body && body.message) return body.message;
      if (body && Array.isArray(body.errors) && body.errors.length) {
        const e = body.errors[0];
        return typeof e === 'string' ? e : (e.message || JSON.stringify(e));
      }
    } catch (_) {}
    return "We couldn't add that to your trolley.";
  }

  async function performJsonAdd(payload, btn, originalText) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const msg = await parseAddError(res);
        showToast(msg);
        return;
      }
      let cart = null;
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (cartRes.ok) {
        cart = await cartRes.json();
        updateHeaderCount(cart.item_count);
      }
      if (cart) {
        const mutated = await reconcileFrozenFee(cart);
        if (mutated) {
          try {
            const fresh = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
            updateHeaderCount(fresh.item_count);
          } catch (_) {}
        }
      }
      await refreshDrawer();
      open();
    } catch (err) {
      showToast("Network error — please try again.");
    } finally {
      if (btn) {
        btn.textContent = originalText;
        btn.disabled = false;
      }
    }
  }

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-card-add-to-cart]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.getAttribute('data-card-add-to-cart');
    if (!variantId) return;
    const isFrozen = btn.getAttribute('data-frozen') === 'true';

    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    const payload = { id: variantId, quantity: 1 };
    if (isFrozen) payload.properties = { _frozen: 'true' };

    await performJsonAdd(payload, btn, original);
  });

  // ---------- add-to-cart form interception (PDP) ----------

  async function performFormAdd(form) {
    const fd = new FormData(form);
    // Lock the submit button(s) inside this form so rapid clicks
    // can't queue multiple /cart/add.js requests in flight.
    const submitBtns = Array.from(form.querySelectorAll('button[type="submit"], input[type="submit"]'));
    const originals = submitBtns.map((b) => ({ btn: b, text: b.textContent, disabled: b.disabled }));
    submitBtns.forEach((b) => {
      b.disabled = true;
      if (b.textContent && !/Adding/i.test(b.textContent)) b.dataset._origText = b.textContent;
      b.textContent = 'Adding…';
    });
    function restoreSubmits() {
      originals.forEach(({ btn, text, disabled }) => {
        btn.disabled = disabled;
        btn.textContent = text;
      });
    }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        const msg = await parseAddError(res);
        showToast(msg);
        return;
      }
      let cart = null;
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (cartRes.ok) {
        cart = await cartRes.json();
        updateHeaderCount(cart.item_count);
      }
      if (cart) {
        const mutated = await reconcileFrozenFee(cart);
        if (mutated) {
          try {
            const fresh = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
            updateHeaderCount(fresh.item_count);
          } catch (_) {}
        }
      }
      await refreshDrawer();
      open();
    } catch (err) {
      showToast("Network error — please try again.");
    } finally {
      restoreSubmits();
    }
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form || form.action.indexOf('/cart/add') === -1) return;
    e.preventDefault();
    await performFormAdd(form);
  });

  // ---------- initial reconcile on page load ----------
  //
  // Covers carts that were populated before this code shipped, OR before
  // the FROZEN_FEE_VARIANT_ID was known. Fires fire-and-forget so it
  // doesn't block first paint.
  (async () => {
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!r.ok) return;
      const cart = await r.json();
      const mutated = await reconcileFrozenFee(cart);
      if (mutated) {
        try {
          const fresh = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
          updateHeaderCount(fresh.item_count);
        } catch (_) {}
        await refreshDrawer();
      }
    } catch (_) {}
  })();
})();
